import { PushError, TimeoutError } from '../exceptions';
import { randomString, sleep } from '../helpers';
import { sha256 } from '../helpers/crypto';
export class PushSession {
    service;
    session;
    config;
    doneFuture = new Future();
    affiliation = randomString(6);
    log;
    running = false;
    retryTimeoutMs;
    tries = 0;
    static get defaultConfig() {
        return {
            retryTimeoutInitMs: 2000,
            retryTimeoutMaxMs: 30000,
            triesMax: 3,
            timeToLiveRange: [0, 15, 90],
        };
    }
    static expectedPeriodMaxMs(config) {
        if (config === undefined) {
            config = PushSession.defaultConfig;
        }
        if (config.triesMax === Number.POSITIVE_INFINITY) {
            return Number.POSITIVE_INFINITY;
        }
        let retryTimeoutMs = config.retryTimeoutInitMs;
        let sumMs = 0;
        for (let i = 0; i < config.triesMax; ++i) {
            sumMs += retryTimeoutMs;
            retryTimeoutMs = Math.min(retryTimeoutMs * 2, config.retryTimeoutMaxMs);
        }
        return sumMs;
    }
    constructor(service, session, config) {
        this.log = service.logService.getLogger(`Push.${this.affiliation}`, 'color: #fff; background-color: #9900cc');
        this.service = service;
        this.session = session;
        this.config = config !== undefined ? config : PushSession.defaultConfig;
        this.retryTimeoutMs = this.config.retryTimeoutInitMs;
        if (this.config.timeToLiveRange.length === 0) {
            throw new Error('timeToLiveRange must not be an empty array');
        }
        if (this.config.triesMax < 1) {
            throw new Error('triesMax must be >= 1');
        }
    }
    start() {
        if (!this.running) {
            this.run().catch((error) => {
                this.log.error('Push runner failed:', error);
                this.doneFuture.reject(error);
            });
            this.running = true;
        }
        return this.doneFuture;
    }
    done() {
        this.log.info('Push done');
        this.doneFuture.resolve();
    }
    async run() {
        const sessionHash = await sha256(this.session.buffer);
        const data = new URLSearchParams();
        data.set(PushService.ARG_TYPE, this.service.pushType);
        data.set(PushService.ARG_SESSION, sessionHash);
        data.set(PushService.ARG_VERSION, `${this.service.version}`);
        data.set(PushService.ARG_AFFILIATION, this.affiliation);
        if (this.service.pushType === "apns") {
            const parts = this.service.pushToken.split(';');
            if (parts.length < 3) {
                throw new Error(`APNS push token contains ${parts.length} parts, but at least 3 are required`);
            }
            data.set(PushService.ARG_TOKEN, parts[0]);
            data.set(PushService.ARG_ENDPOINT, parts[1]);
            data.set(PushService.ARG_BUNDLE_ID, parts[2]);
        }
        else if (this.service.pushType === "gcm") {
            data.set(PushService.ARG_TOKEN, this.service.pushToken);
        }
        else if (this.service.pushType === "hms") {
            const parts = this.service.pushToken.split('|');
            data.set(PushService.ARG_TOKEN, parts[1]);
            data.set(PushService.ARG_APP_ID, parts[0]);
        }
        else if (this.service.pushType === "threema-gateway") {
            const parts = this.service.pushToken.split(';');
            data.set('identity', parts[0]);
            data.set('public_key', parts[1]);
        }
        else {
            throw new Error(`Invalid push type: ${this.service.pushType}`);
        }
        while (!this.doneFuture.done) {
            let timeToLive = this.config.timeToLiveRange[this.tries];
            if (timeToLive === undefined) {
                timeToLive = this.config.timeToLiveRange[this.config.timeToLiveRange.length - 1];
            }
            if (timeToLive === 0) {
                data.delete(PushService.ARG_COLLAPSE_KEY);
            }
            else {
                data.set(PushService.ARG_COLLAPSE_KEY, sessionHash.slice(0, 6));
            }
            data.set(PushService.ARG_TIME_TO_LIVE, `${timeToLive}`);
            ++this.tries;
            this.log.debug(`Sending push ${this.tries}/${this.config.triesMax} (ttl=${timeToLive})`);
            if (this.service.userConfig.ARP_LOG_TRACE) {
                this.log.debug('Push data:', `${data}`);
            }
            try {
                const response = await fetch(this.service.url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: data,
                });
                if (response.ok) {
                    this.log.debug('Push sent successfully');
                }
                else if (response.status >= 400 && response.status < 500) {
                    const error = `Push rejected (client error), status: ${response.status}`;
                    this.log.warn(error);
                    this.doneFuture.reject(new PushError(error, response.status));
                }
                else {
                    this.log.warn(`Push rejected (server error), status: ${response.status}`);
                }
            }
            catch (error) {
                this.log.warn('Sending push failed:', error);
            }
            await sleep(this.retryTimeoutMs);
            this.retryTimeoutMs = Math.min(this.retryTimeoutMs * 2, this.config.retryTimeoutMaxMs);
            if (!this.doneFuture.done && this.tries === this.config.triesMax) {
                const error = `Push session timeout after ${this.tries} tries`;
                this.log.warn(error);
                this.doneFuture.reject(new TimeoutError(error));
            }
        }
    }
}
export class PushService {
    static ARG_TYPE = 'type';
    static ARG_TOKEN = 'token';
    static ARG_SESSION = 'session';
    static ARG_VERSION = 'version';
    static ARG_AFFILIATION = 'affiliation';
    static ARG_APP_ID = 'appid';
    static ARG_ENDPOINT = 'endpoint';
    static ARG_BUNDLE_ID = 'bundleid';
    static ARG_TIME_TO_LIVE = 'ttl';
    static ARG_COLLAPSE_KEY = 'collapse_key';
    config;
    userConfig;
    url;
    version = null;
    logService;
    log;
    _pushToken = null;
    _pushType = "gcm";
    static $inject = ['CONFIG', 'PROTOCOL_VERSION', 'LogService'];
    constructor(CONFIG, PROTOCOL_VERSION, logService) {
        this.config = CONFIG;
        this.userConfig = window['UserConfig'];
        this.url = this.userConfig.PUSH_URL;
        this.version = PROTOCOL_VERSION;
        this.logService = logService;
        this.log = logService.getLogger(`Push-S`, 'color: #fff; background-color: #9900ff');
    }
    get pushToken() {
        return this._pushToken;
    }
    get pushType() {
        return this._pushType;
    }
    init(pushToken, pushTokenType) {
        this.log.info('Initialized with', pushTokenType, 'token');
        this._pushToken = pushToken;
        this._pushType = pushTokenType;
    }
    reset() {
        this._pushToken = null;
    }
    isAvailable() {
        return this._pushToken != null;
    }
    createSession(session, config) {
        if (!this.isAvailable()) {
            throw new Error('Push service unavailable');
        }
        return new PushSession(this, session, config);
    }
}
